'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import Image from 'next/image';
import { useTranslations, useLocale } from 'next-intl';
import { Link, usePathname, useRouter, locales, localeNames, localeFlags, type Locale } from '@medizzconnect/i18n';
import { Show, UserButton, useAuth } from '@medizzconnect/auth';
import { cn } from '@medizzconnect/shared';
import { LanguageSwitcher } from './language-switcher';
import { AuthModal } from '@/components/auth/auth-modal';
import { useIsAdmin } from '@/hooks/use-is-admin';
import { useUserRole } from '@/hooks/use-user-role';
import { HelpContactDialog } from '@/components/shared/help-contact-dialog';
import { useFilterStore } from '@/app/[locale]/_components/filters/filter-store';

const SCROLL_THRESHOLD = 48;

type AuthVariant = 'default' | 'merkliste' | 'mediziner';
type IDBFactoryWithDatabases = IDBFactory & {
  databases?: () => Promise<Array<{ name?: string }>>;
};

interface HeaderProps {
  /** 'transparent' = transparenter Header über Hero (Home-Page), 'default' = normaler weißer Header */
  variant?: 'default' | 'transparent';
}

export function Header({ variant = 'default' }: HeaderProps) {
  const t = useTranslations('nav');
  const tCommon = useTranslations('common');
  const pathname = usePathname();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [medicalOpen, setMedicalOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<'login' | 'register'>('login');
  const [authModalVariant, setAuthModalVariant] = useState<AuthVariant>('default');
  const [isClearingBrowserData, setIsClearingBrowserData] = useState(false);
  const { isAdmin: userIsAdmin } = useIsAdmin();
  const { roleType } = useUserRole();
  const { isSignedIn } = useAuth();
  const resetFilters = useFilterStore((state) => state.resetFilters);
  const locale = useLocale() as Locale;
  const router = useRouter();

  // Merkliste nur für client-Rolle (oder nicht eingeloggt)
  const showWatchlist = !roleType || roleType === 'client';

  // ── Dropdown Refs ──
  const aboutRef = useRef<HTMLDivElement>(null);
  const medicalRef = useRef<HTMLDivElement>(null);

  // ── Drawer Touch/Swipe ──
  const drawerRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number>(0);
  const touchDeltaX = useRef<number>(0);

  const isTransparent = variant === 'transparent';

  // Scroll-Detection
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > SCROLL_THRESHOLD);
    setScrolled(window.scrollY > SCROLL_THRESHOLD);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Mobile-Menu schließen bei Route-Change
  useEffect(() => {
    setMobileMenuOpen(false);
    setAboutOpen(false);
    setMedicalOpen(false);
  }, [pathname]);

  // Body-Scroll sperren bei offenem Mobile-Menu
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (aboutRef.current && !aboutRef.current.contains(e.target as Node)) {
        setAboutOpen(false);
      }
      if (medicalRef.current && !medicalRef.current.contains(e.target as Node)) {
        setMedicalOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── Swipe-to-Close ──
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0]?.clientX ?? 0;
    touchDeltaX.current = 0;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const currentX = e.touches[0]?.clientX ?? 0;
    touchDeltaX.current = currentX - touchStartX.current;
    if (touchDeltaX.current < 0 && drawerRef.current) {
      drawerRef.current.style.transform = `translateX(${touchDeltaX.current}px)`;
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (touchDeltaX.current < -80) {
      setMobileMenuOpen(false);
    }
    if (drawerRef.current) {
      drawerRef.current.style.transform = '';
    }
    touchDeltaX.current = 0;
  }, []);

  // ── Auth Modal Helpers ──
  const openAuthModal = useCallback((mode: 'login' | 'register', authVariant: AuthVariant = 'default') => {
    // Eingeloggte User direkt zum Dashboard weiterleiten
    if (isSignedIn) {
      setMobileMenuOpen(false);
      setMedicalOpen(false);
      router.push('/dashboard');
      return;
    }
    setAuthModalMode(mode);
    setAuthModalVariant(authVariant);
    setAuthModalOpen(true);
    setMobileMenuOpen(false);
    setMedicalOpen(false);
  }, [isSignedIn, router]);

  const isActive = (href: string) =>
    href === '/' ? pathname === '/' : pathname?.startsWith(href);

  const handleLogoClick = useCallback(() => {
    resetFilters();
    setAboutOpen(false);
    setMedicalOpen(false);
    setMobileMenuOpen(false);
  }, [resetFilters]);

  // ── Browser-Daten löschen + Hard-Reload (für schnellen Update-Fetch) ──
  const clearBrowserDataAndReload = useCallback(async () => {
    if (isClearingBrowserData) return;

    const confirmText = tCommon('clearBrowserDataConfirm');

    if (!window.confirm(confirmText)) return;

    setIsClearingBrowserData(true);
    setAboutOpen(false);
    setMedicalOpen(false);
    setMobileMenuOpen(false);

    const clearAccessibleCookies = () => {
      try {
        const cookies = document.cookie ? document.cookie.split(';') : [];
        const host = window.location.hostname;
        const rootDomain = host.includes('.') ? host.split('.').slice(-2).join('.') : host;

        for (const cookie of cookies) {
          const name = cookie.split('=')[0]?.trim();
          if (!name) continue;
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${host}`;
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${rootDomain}`;
        }
      } catch {}
    };

    const deleteKnownIndexedDb = async () => {
      try {
        const idb = window.indexedDB as IDBFactoryWithDatabases;
        if (typeof idb.databases !== 'function') return;

        const dbs = await idb.databases();
        await Promise.allSettled(
          (dbs ?? []).map((db) => {
            if (!db.name) return Promise.resolve();
            return new Promise<void>((resolve) => {
              const req = idb.deleteDatabase(db.name!);
              req.onsuccess = () => resolve();
              req.onerror = () => resolve();
              req.onblocked = () => resolve();
            });
          }),
        );
      } catch {}
    };

    try {
      try { localStorage.clear(); } catch {}
      try { sessionStorage.clear(); } catch {}
      clearAccessibleCookies();

      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.allSettled(keys.map((key) => caches.delete(key)));
      }

      await deleteKnownIndexedDb();

      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.allSettled(regs.map((reg) => reg.unregister()));
      }

      const url = new URL(window.location.href);
      url.searchParams.set('refresh', String(Date.now()));
      window.location.replace(url.toString());
    } catch {
      window.location.reload();
    }
  }, [isClearingBrowserData, tCommon]);

  // ── Dropdown Items ──
  const aboutItems = [
    { href: '/about', label: t('about') },
    { href: '/faq', label: t('faq') },
    { href: '/datenschutz', label: t('privacy') },
  ];

  // Mediziner-Dropdown: Registrieren → /onboarding, Anmelden → Mediziner-Auth-Modal, Preise
  const medicalItems = [
    { href: '/onboarding', label: t('doctorRegister'), type: 'link' as const },
    { href: '#mediziner-login', label: t('doctorLogin'), type: 'modal' as const },
    { href: '/start', label: 'IntroCam™', type: 'link' as const },
    { href: '/pricing', label: t('pricing'), type: 'link' as const },
  ];

  // ── Logo Logic ──
  const useWhiteLogo = isTransparent && !scrolled;
  const logoSrc = useWhiteLogo
    ? '/images/Medizzconnect_Logo_White_tm.webp'
    : '/images/Medizzconnect_Logo_Black_tm.webp';

  // ── Header Styles ──
  const headerBg = isTransparent && !scrolled
    ? 'bg-white/10 border-b border-white/15 backdrop-blur-[10px]'
    : 'bg-white border-b border-black/10 backdrop-blur-md shadow-[0_4px_20px_rgba(0,0,0,0.08)]';

  const textSecondary = isTransparent && !scrolled
    ? 'text-white/80 hover:text-white hover:bg-white/10'
    : 'text-text-secondary hover:text-text-primary hover:bg-bg-subtle';

  return (
    <>
      <header
        className={cn(
          'fixed top-0 left-0 right-0 z-[1000] w-full transition-all duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)]',
          headerBg,
        )}
      >
        <div className="mx-auto flex h-[var(--header-height-mobile)] sm:h-[var(--header-height)] max-w-content items-center justify-between px-4 sm:px-6 lg:px-8">
          {/* ── Logo ── */}
          <Link href="/" className="flex items-center gap-2 flex-shrink-0" onClick={handleLogoClick}>
            <Image
              src={logoSrc}
              alt="medizzconnect™"
              width={160}
              height={40}
              className="h-10 w-auto transition-transform duration-200 hover:scale-[1.04]"
              priority
            />
          </Link>

          {/* ── Desktop Navigation ── */}
          <nav className="hidden lg:flex items-center gap-1" aria-label="Hauptnavigation">
            {/* Über uns Dropdown */}
            <div
              ref={aboutRef}
              className="relative"
              onMouseEnter={() => { setAboutOpen(true); setMedicalOpen(false); }}
              onMouseLeave={() => setAboutOpen(false)}
            >
              <button
                type="button"
                onClick={() => { setAboutOpen(!aboutOpen); setMedicalOpen(false); }}
                className={cn(
                  'flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-150',
                  textSecondary,
                )}
                aria-expanded={aboutOpen}
              >
                {t('about')}
                <svg className={cn('h-3.5 w-3.5 transition-transform duration-150', aboutOpen && 'rotate-180')} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {aboutOpen && (
                <>
                  {/* Bridge-Element: unsichtbare Brücke zwischen Button und Dropdown */}
                  <div className="absolute left-0 top-full w-48 h-2" aria-hidden="true" />
                  <div className="absolute left-0 top-full pt-2 w-48 z-[1010]">
                    <div className="rounded-xl border border-border-light bg-white py-1 shadow-lg">
                      {aboutItems.map((item) => (
                        <Link
                          key={item.href}
                          href={item.href}
                          className="block px-4 py-2.5 text-sm text-text-primary hover:bg-bg-subtle transition-colors"
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Für Medizinerinnen Dropdown */}
            <div
              ref={medicalRef}
              className="relative"
              onMouseEnter={() => { setMedicalOpen(true); setAboutOpen(false); }}
              onMouseLeave={() => setMedicalOpen(false)}
            >
              <button
                type="button"
                onClick={() => { setMedicalOpen(!medicalOpen); setAboutOpen(false); }}
                className={cn(
                  'flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-150',
                  textSecondary,
                )}
                aria-expanded={medicalOpen}
              >
                {t('forDoctors')}
                <svg className={cn('h-3.5 w-3.5 transition-transform duration-150', medicalOpen && 'rotate-180')} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {medicalOpen && (
                <>
                  {/* Bridge-Element: unsichtbare Brücke zwischen Button und Dropdown */}
                  <div className="absolute left-0 top-full w-52 h-2" aria-hidden="true" />
                  <div className="absolute left-0 top-full pt-2 w-52 z-[1010]">
                    <div className="rounded-xl border border-border-light bg-white py-1 shadow-lg">
                      {medicalItems.map((item) =>
                        item.type === 'modal' ? (
                          <button
                            key={item.href}
                            onClick={() => openAuthModal('login', 'mediziner')}
                            className="block w-full text-left px-4 py-2.5 text-sm text-text-primary hover:bg-bg-subtle transition-colors"
                          >
                            {item.label}
                          </button>
                        ) : (
                          <Link
                            key={item.href}
                            href={item.href}
                            className="block px-4 py-2.5 text-sm text-text-primary hover:bg-bg-subtle transition-colors"
                          >
                            {item.label}
                          </Link>
                        )
                      )}
                      <div className="mx-3 my-1 h-px bg-border-light" />
                      <HelpContactDialog
                        className="block w-full text-left px-4 py-2.5 text-sm text-text-secondary hover:bg-bg-subtle transition-colors"
                      >
                        {t('contact')}
                      </HelpContactDialog>
                    </div>
                  </div>
                </>
              )}
            </div>

            <LanguageSwitcher />

            {/* Blog (standalone wie V1) */}
            <Link
              href="/blog"
              className={cn(
                'px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-150',
                isActive('/blog')
                  ? 'text-primary-600 bg-primary-50'
                  : textSecondary,
              )}
            >
              {t('blog')}
            </Link>

            {/* Meine Merkliste Button — nur für client-Rolle */}
            {showWatchlist && (
              <>
                 <Show when="signed-in">
                  <Link
                    href="/dashboard/favorites"
                    className={cn(
                      'inline-flex h-9 items-center gap-1.5 rounded-lg px-4 text-sm font-medium transition-colors duration-200',
                      isTransparent && !scrolled
                        ? 'bg-primary-500/80 text-white hover:bg-primary-500'
                        : 'bg-primary-500 text-white hover:bg-primary-600',
                    )}
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
                    </svg>
                    {t('watchlist')}
                  </Link>
                </Show>
                <Show when="signed-out">
                  <button
                    onClick={() => openAuthModal('login', 'merkliste')}
                    className={cn(
                      'inline-flex h-9 items-center gap-1.5 rounded-lg px-4 text-sm font-medium transition-colors duration-200',
                      isTransparent && !scrolled
                        ? 'bg-primary-500/80 text-white hover:bg-primary-500'
                        : 'bg-primary-500 text-white hover:bg-primary-600',
                    )}
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
                    </svg>
                    {t('watchlist')}
                  </button>
                </Show>
              </>
            )}
          </nav>

          {/* ── Right Side ── */}
          <div className="flex items-center gap-2 sm:gap-3">
            <Show when="signed-out">
              {/* Info Tooltip (Geschlechtsneutralitäts-Hinweis wie V1) */}
              <span className="hidden lg:inline-flex relative group">
                <span className="inline-flex h-9 w-9 items-center justify-center cursor-help">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="12" cy="12" r="10" stroke={isTransparent && !scrolled ? 'white' : 'currentColor'} strokeWidth="2" fill="rgba(255,255,255,0.9)" />
                    <path d="M12,11 C11.4,11 11,11.4 11,12 L11,16 C11,16.6 11.4,17 12,17 C12.6,17 13,16.6 13,16 L13,12 C13,11.4 12.6,11 12,11 Z" fill="#38b2ac" />
                    <circle cx="12" cy="8.5" r="1.5" fill="#38b2ac" />
                  </svg>
                </span>
                <div className="absolute right-0 top-full mt-2 w-72 rounded-xl border border-border-light bg-white p-4 shadow-lg z-[1010] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <div className="absolute -top-2 right-4 w-3 h-3 bg-white border-l border-t border-border-light rotate-45" />
                  <p className="text-sm font-semibold text-text-primary mb-1">{t('infoTitle')}</p>
                  <p className="text-xs text-text-secondary leading-relaxed">{t('infoDescription')}</p>
                </div>
              </span>
            </Show>

            <Show when="signed-in">
              {userIsAdmin && (
                <Link
                  href="/admin"
                  className={cn(
                    'hidden lg:inline-flex h-9 items-center gap-1.5 rounded-lg px-3 text-sm font-medium transition-colors duration-150',
                    textSecondary,
                  )}
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 010 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 010-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Admin
                </Link>
              )}
              <Link
                href="/dashboard"
                className={cn(
                  'hidden lg:inline-flex h-9 items-center gap-1.5 rounded-lg px-3 text-sm font-medium transition-colors duration-150',
                  textSecondary,
                )}
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
                </svg>
                {t('dashboard')}
              </Link>
              <UserButton
                appearance={{
                  elements: {
                    avatarBox: 'h-8 w-8',
                  },
                }}
              />
            </Show>

            {/* Unauffälliger Update-Reset-Button */}
            <button
              type="button"
              onClick={clearBrowserDataAndReload}
              disabled={isClearingBrowserData}
              className={cn(
                'flex h-9 w-9 items-center justify-center rounded-lg transition-colors disabled:opacity-60',
                isTransparent && !scrolled
                  ? 'text-white/90 hover:bg-white/10'
                  : 'text-text-secondary hover:bg-bg-subtle',
              )}
              title={tCommon('clearBrowserDataLabel')}
              aria-label={tCommon('clearBrowserDataAria')}
            >
              {isClearingBrowserData ? (
                <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12a7.5 7.5 0 0113.305-4.778M19.5 12a7.5 7.5 0 01-13.305 4.778" />
                </svg>
              ) : (
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992V4.356M2.977 14.652H7.97v4.992" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 12a9 9 0 0114.13-7.46l2.885 2.29M21 12a9 9 0 01-14.13 7.46l-2.885-2.29" />
                </svg>
              )}
            </button>

            {/* ── Mobile Menu Toggle ── */}
            <button
              className={cn(
                'flex lg:hidden h-9 w-9 items-center justify-center rounded-lg transition-colors',
                isTransparent && !scrolled
                  ? 'text-white hover:bg-white/10'
                  : 'text-text-secondary hover:bg-bg-subtle',
              )}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Menü schließen' : 'Menü öffnen'}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* ── Spacer für fixed Header ── */}
      {!isTransparent && (
        <div className="h-[var(--header-height-mobile)] sm:h-[var(--header-height)]" aria-hidden="true" />
      )}

      {/* ── Mobile Drawer ── */}
      {mobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-[39] bg-black/50 lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
            aria-hidden="true"
          />

          {/* Drawer */}
          <div
            ref={drawerRef}
            className="fixed top-[var(--header-height-mobile)] left-0 bottom-0 z-40 w-[85vw] max-w-[320px] bg-white lg:hidden overflow-y-auto safe-bottom animate-slide-in-left"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <nav className="flex flex-col p-4 gap-1" aria-label="Mobile Navigation">

              {/* ── Sprachwechsler (Mobile) ── */}
              <div className="flex items-center gap-1.5 px-2 pb-3 mb-1 border-b border-border-light">
                {locales.map((l) => (
                  <button
                    key={l}
                    onClick={() => {
                      router.replace(pathname, { locale: l });
                      setMobileMenuOpen(false);
                    }}
                    className={cn(
                      'flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors',
                      l === locale
                        ? 'bg-primary-50 text-primary-600 ring-1 ring-primary-200'
                        : 'text-text-secondary hover:bg-bg-subtle',
                    )}
                    aria-label={localeNames[l]}
                  >
                    <span className="text-base">{localeFlags[l]}</span>
                    <span className="text-xs">{l.toUpperCase()}</span>
                  </button>
                ))}
              </div>

              <Link
                href="/"
                className={cn(
                  'flex items-center rounded-xl px-4 py-3 text-base font-medium transition-colors',
                  isActive('/')
                    ? 'text-primary-600 bg-primary-50'
                    : 'text-text-primary hover:bg-bg-subtle',
                )}
              >
                {t('home')}
              </Link>

              {/* Über uns */}
              <span className="px-4 pt-4 pb-1 text-xs font-semibold text-text-muted uppercase tracking-wider">{t('about')}</span>
              {aboutItems.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className="flex items-center rounded-xl px-4 py-3 text-base font-medium text-text-primary hover:bg-bg-subtle"
                >
                  {item.label}
                </Link>
              ))}

              {/* Für Medizinerinnen */}
              <span className="px-4 pt-4 pb-1 text-xs font-semibold text-text-muted uppercase tracking-wider">{t('forDoctors')}</span>
              <Link
                href="/onboarding"
                className="flex items-center rounded-xl px-4 py-3 text-base font-medium text-text-primary hover:bg-bg-subtle"
              >
                {t('doctorRegister')}
              </Link>
              <button
                onClick={() => openAuthModal('login', 'mediziner')}
                className="flex items-center rounded-xl px-4 py-3 text-base font-medium text-text-primary hover:bg-bg-subtle w-full text-left"
              >
                {t('doctorLogin')}
              </button>
              <Link
                href="/start"
                className="flex items-center rounded-xl px-4 py-3 text-base font-medium text-text-primary hover:bg-bg-subtle"
              >
                IntroCam™
              </Link>
              <Link
                href="/pricing"
                className="flex items-center rounded-xl px-4 py-3 text-base font-medium text-text-primary hover:bg-bg-subtle"
              >
                {t('pricing')}
              </Link>
              <HelpContactDialog
                className="flex items-center gap-2 rounded-xl px-4 py-3 text-base font-medium text-text-secondary hover:bg-bg-subtle w-full text-left"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                </svg>
                {t('contact')}
              </HelpContactDialog>

              <Link
                href="/blog"
                className={cn(
                  'flex items-center rounded-xl px-4 py-3 text-base font-medium transition-colors',
                  isActive('/blog')
                    ? 'text-primary-600 bg-primary-50'
                    : 'text-text-primary hover:bg-bg-subtle',
                )}
              >
                {t('blog')}
              </Link>

              <hr className="my-3 border-border-light" />

              {/* Merkliste — nur für client-Rolle */}
              {showWatchlist && (
                <>
                  <Show when="signed-in">
                    <Link
                      href="/dashboard/favorites"
                      className="flex items-center gap-2 rounded-xl px-4 py-3 text-base font-medium text-primary-600 hover:bg-primary-50"
                    >
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
                      </svg>
                      {t('watchlist')}
                    </Link>
                  </Show>
                  <Show when="signed-out">
                    <button
                      onClick={() => openAuthModal('login', 'merkliste')}
                      className="flex items-center gap-2 rounded-xl px-4 py-3 text-base font-medium text-primary-600 hover:bg-primary-50 w-full text-left"
                    >
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
                      </svg>
                      {t('watchlist')}
                    </button>
                  </Show>
                </>
              )}

              <hr className="my-3 border-border-light" />

              <Show when="signed-out">
                <button
                  onClick={() => openAuthModal('login')}
                  className="flex items-center rounded-xl px-4 py-3 text-base font-medium text-text-primary hover:bg-bg-subtle w-full text-left"
                >
                  {tCommon('login')}
                </button>
                <button
                  onClick={() => openAuthModal('register')}
                  className="flex items-center justify-center rounded-xl bg-primary-500 px-4 py-3 text-base font-medium text-white hover:bg-primary-600 mt-2 w-full"
                >
                  {tCommon('register')}
                </button>
              </Show>

              <Show when="signed-in">
                {userIsAdmin && (
                  <Link
                    href="/admin"
                    className="flex items-center gap-2 rounded-xl px-4 py-3 text-base font-medium text-primary-600 hover:bg-primary-50"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 010 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 010-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Admin
                  </Link>
                )}
                <Link
                  href="/dashboard"
                  className="flex items-center gap-2 rounded-xl px-4 py-3 text-base font-medium text-text-primary hover:bg-bg-subtle"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
                  </svg>
                  {t('dashboard')}
                </Link>
                <Link
                  href="/dashboard/profile"
                  className="flex items-center gap-2 rounded-xl px-4 py-3 text-base font-medium text-text-primary hover:bg-bg-subtle"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                  </svg>
                  {t('profile')}
                </Link>
                <Link
                  href="/dashboard/settings"
                  className="flex items-center gap-2 rounded-xl px-4 py-3 text-base font-medium text-text-primary hover:bg-bg-subtle"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {t('settings')}
                </Link>
              </Show>
            </nav>
          </div>
        </>
      )}

      {/* ── Auth Modal ── */}
      {authModalOpen && (
        <AuthModal
          mode={authModalMode}
          variant={authModalVariant}
          onClose={() => setAuthModalOpen(false)}
        />
      )}
    </>
  );
}
